import React, { useState, useEffect } from 'react';
import {
    TextField, FormControl, Autocomplete, CircularProgress, createFilterOptions
} from '@mui/material';
import { getCondoId } from '../../../services/Main/AuthStorage';
import { api } from '../../../services/Main/Api';

const filter = createFilterOptions({
    stringify: (option) => option.name || '',
});

const CategoryGroupSelect = ({ value, onChange, error, helperText, ...props }) => {
    const [inputValue, setInputValue] = useState('');
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);

    useEffect(() => {
        setLoading(true);
        api.get('/cashflow/category-groups/', {
            params: { condo_id: getCondoId(), active: true }
        })
            .then(res => {
                const results = res.data?.results || res.data || [];
                setOptions(results.map(item => ({ id: item.id, name: item.name })));
            })
            .catch(err => console.error('Erro ao buscar grupos:', err))
            .finally(() => setLoading(false));
    }, []);

    const handleCreateGroup = async (name) => {
        if (!name.trim()) return;
        setCreating(true);
        try {
            const res = await api.post('/cashflow/category-groups/', {
                name,
                condo: getCondoId(),
            });
            const newGroup = { id: res.data.id, name: res.data.name };
            setOptions(prev => [...prev, newGroup]);
            onChange(newGroup.id);
            setInputValue(newGroup.name);
        } catch (err) {
            console.error('Erro ao criar grupo:', err);
        } finally {
            setCreating(false);
        }
    };

    const handleChange = (event, newValue) => {
        if (typeof newValue === 'string') {
            handleCreateGroup(newValue);
        } else if (newValue && newValue.inputValue) {
            handleCreateGroup(newValue.inputValue);
        } else if (newValue && newValue.id) {
            onChange(newValue.id);
        } else {
            onChange('');
        }
    };

    const selectedOption = options.find(item => item.id === value) || null;

    return (
        <FormControl fullWidth>
            <Autocomplete
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                freeSolo
                value={selectedOption}
                onChange={handleChange}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                options={options}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.inputValue) return option.inputValue;
                    return option.name || '';
                }}
                filterOptions={(opts, params) => {
                    const filtered = filter(opts, params);
                    const isExisting = opts.some(opt => params.inputValue === opt.name);
                    if (params.inputValue !== '' && !isExisting) {
                        filtered.push({
                            inputValue: params.inputValue,
                            name: `Adicionar "${params.inputValue}"`,
                        });
                    }
                    return filtered;
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id || option.inputValue}>
                        {option.inputValue ? `➕ Adicionar "${option.inputValue}"` : option.name}
                    </li>
                )}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                loading={creating || loading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Grupo"
                        variant="outlined"
                        size="small"
                        error={error}
                        helperText={helperText}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': { borderColor: '#E2E8F0' },
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {(creating || loading) ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                {...props}
            />
        </FormControl>
    );
};

export default CategoryGroupSelect;
